// src/pages/finance-portal/fixed-assets/Disposals.js
//
// Asset Disposals

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Grid, IconButton, MenuItem, Paper, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK, DISPOSAL_STATUS_COLORS, DISPOSAL_TYPES,
    formatCurrency, formatDate, statusChip, humanizeStatus,
} from './components/fixedAssetFormatters';

const headSx = { fontFamily: FA_FONT, fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' };
const cellSx = { fontFamily: FA_FONT, fontSize: '0.84rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' };

export default function Disposals() {
    const { enqueueSnackbar } = useSnackbar();
    const [rows, setRows] = useState([]);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState({
        asset_id: '', disposal_type: 'SALE',
        disposal_date: new Date().toISOString().slice(0, 10),
        proceeds: '', disposal_costs: '', buyer_name: '', notes: '',
    });
    const [errors, setErrors] = useState({});

    const setField = (k, v) => {
        setForm(prev => ({ ...prev, [k]: v }));
        if (errors[k]) setErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };

    const validateForm = () => {
        const e = {};
        if (!form.asset_id) e.asset_id = 'Select an asset';
        if (!form.disposal_date) e.disposal_date = 'Disposal date is required';

        const proceeds = parseFloat(form.proceeds);
        if (form.proceeds !== '' && form.proceeds !== null
            && (Number.isNaN(proceeds) || proceeds < 0)) {
            e.proceeds = 'Proceeds must be non-negative';
        }
        const costs = parseFloat(form.disposal_costs);
        if (form.disposal_costs !== '' && form.disposal_costs !== null
            && (Number.isNaN(costs) || costs < 0)) {
            e.disposal_costs = 'Disposal costs must be non-negative';
        }

        // For donation/scrap/abandonment, proceeds should be 0
        if (['DONATION', 'SCRAP', 'ABANDONMENT'].includes(form.disposal_type)
            && proceeds > 0) {
            e.proceeds = `Proceeds should be 0 for ${form.disposal_type.toLowerCase()}`;
        }
        // Sale typically has proceeds
        if (form.disposal_type === 'SALE' && (!form.proceeds || proceeds <= 0)) {
            e.proceeds = 'Sale disposals usually have positive proceeds';
        }
        return e;
    };

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/fixed-assets/disposals',
                { params: { per_page: 100 } });
            if (res.data?.success) setRows(res.data.data || []);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Failed to load disposals', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        fetchRows();
        axiosInstance.get('/api/finance/fixed-assets/assets',
            { params: { status: 'ACTIVE', per_page: 200 } })
            .then(r => { if (r.data?.success) setAssets(r.data.data || []); })
            .catch(() => {});
    }, [fetchRows]);

    const handleCreate = async () => {
        const errs = validateForm();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            enqueueSnackbar(`Fix ${Object.keys(errs).length} validation error(s)`, { variant: 'warning' });
            return;
        }
        setErrors({});
        try {
            const payload = { ...form,
                proceeds: parseFloat(form.proceeds) || 0,
                disposal_costs: parseFloat(form.disposal_costs) || 0,
            };
            const res = await axiosInstance.post('/api/finance/fixed-assets/disposals', payload);
            if (res.data?.success) {
                enqueueSnackbar('Disposal recorded', { variant: 'success' });
                setCreateOpen(false);
                setForm({ asset_id: '', disposal_type: 'SALE',
                    disposal_date: new Date().toISOString().slice(0, 10),
                    proceeds: '', disposal_costs: '', buyer_name: '', notes: '' });
                fetchRows();
            }
        } catch (e) {
            const serverErrors = e.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(prev => ({ ...prev, ...serverErrors }));
                enqueueSnackbar(e.response?.data?.message || 'Validation failed on server',
                    { variant: 'warning' });
            } else {
                enqueueSnackbar(e.response?.data?.message || 'Failed to create', { variant: 'error' });
            }
        }
    };

    const handlePost = async (id) => {
        if (!window.confirm('Post this disposal? This retires the asset.')) return;
        try {
            const res = await axiosInstance.post(`/api/finance/fixed-assets/disposals/${id}/post`);
            if (res.data?.success) {
                enqueueSnackbar('Disposal posted', { variant: 'success' });
                fetchRows();
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Post failed', { variant: 'error' });
        }
    };

    const handleCancel = async (id) => {
        if (!window.confirm('Cancel this disposal?')) return;
        try {
            await axiosInstance.post(`/api/finance/fixed-assets/disposals/${id}/cancel`);
            enqueueSnackbar('Disposal cancelled', { variant: 'success' });
            fetchRows();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Cancel failed', { variant: 'error' });
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DeleteSweepIcon sx={{ fontSize: 30, color: '#fff' }} />
                        <Typography sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: '1.6rem' }}>
                            Asset Disposals
                        </Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                        sx={{ fontFamily: FA_FONT, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: FA_ACCENT, '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' } }}>
                        New Disposal
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Disposal #', 'Asset', 'Type', 'Date', 'Proceeds', 'Book Value', 'Gain/Loss', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? [1, 2, 3].map(i => (
                                    <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8, 9].map(j => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                                )) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>No disposals</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map(d => (
                                    <TableRow key={d.id} hover>
                                        <TableCell sx={{ ...cellSx, fontWeight: 600, color: FA_ACCENT_DARK }}>{d.disposal_number}</TableCell>
                                        <TableCell sx={cellSx}>{d.asset_name} <Typography component="span" sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>({d.asset_number})</Typography></TableCell>
                                        <TableCell sx={cellSx}>{humanizeStatus(d.disposal_type)}</TableCell>
                                        <TableCell sx={cellSx}>{formatDate(d.disposal_date)}</TableCell>
                                        <TableCell sx={cellSx}>{formatCurrency(d.proceeds)}</TableCell>
                                        <TableCell sx={cellSx}>{formatCurrency(d.book_value_at_disposal)}</TableCell>
                                        <TableCell sx={{ ...cellSx, fontWeight: 600,
                                            color: d.gain_loss > 0 ? '#166534' : (d.gain_loss < 0 ? '#dc2626' : '#475569') }}>
                                            {formatCurrency(d.gain_loss)}
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            <Chip size="small" label={humanizeStatus(d.status)}
                                                sx={{ fontFamily: FA_FONT, fontWeight: 600, ...statusChip(d.status, DISPOSAL_STATUS_COLORS) }} />
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            {d.status !== 'POSTED' && d.status !== 'CANCELLED' && (
                                                <>
                                                    <Tooltip title="Post">
                                                        <IconButton size="small" onClick={() => handlePost(d.id)}>
                                                            <PlayCircleIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                        <IconButton size="small" onClick={() => handleCancel(d.id)}>
                                                            <CancelIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: FA_FONT, fontWeight: 700 }}>Record Disposal</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" select label="Asset *" required
                                value={form.asset_id}
                                onChange={e => setField('asset_id', e.target.value)}
                                error={Boolean(errors.asset_id)} helperText={errors.asset_id || ' '}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                <MenuItem value="" sx={{ fontFamily: FA_FONT }}>Select an active asset</MenuItem>
                                {assets.map(a => (
                                    <MenuItem key={a.id} value={a.id} sx={{ fontFamily: FA_FONT }}>
                                        {a.asset_number} — {a.name} (BV {formatCurrency(a.book_value)})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" select label="Type"
                                value={form.disposal_type}
                                onChange={e => setField('disposal_type', e.target.value)}
                                helperText=" "
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                {DISPOSAL_TYPES.map(t => (
                                    <MenuItem key={t.value} value={t.value} sx={{ fontFamily: FA_FONT }}>{t.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="date" label="Disposal Date *" required
                                value={form.disposal_date}
                                onChange={e => setField('disposal_date', e.target.value)}
                                error={Boolean(errors.disposal_date)} helperText={errors.disposal_date || ' '}
                                InputLabelProps={{ shrink: true, sx: { fontFamily: FA_FONT } }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="number" label="Proceeds"
                                value={form.proceeds}
                                onChange={e => setField('proceeds', e.target.value)}
                                error={Boolean(errors.proceeds)} helperText={errors.proceeds || ' '}
                                inputProps={{ min: 0, step: '0.01' }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="number" label="Disposal Costs"
                                value={form.disposal_costs}
                                onChange={e => setField('disposal_costs', e.target.value)}
                                error={Boolean(errors.disposal_costs)} helperText={errors.disposal_costs || ' '}
                                inputProps={{ min: 0, step: '0.01' }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Buyer / Counterparty"
                                value={form.buyer_name}
                                onChange={e => setField('buyer_name', e.target.value)}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" multiline minRows={2} label="Notes"
                                value={form.notes}
                                onChange={e => setField('notes', e.target.value)}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCreateOpen(false)} sx={{ fontFamily: FA_FONT }}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained"
                        sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                            '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>
                        Record
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
